/**
 * The change log, and the only place a mistake gets undone.
 *
 * Whether a row offers Revert comes from the server's `revertible` flag and nothing else. The panel does
 * NOT hold its own copy of the revertible set: the endpoint decides from three separate conditions (the
 * action, whether `before` describes a prior state, and whether a correction already exists), and any
 * frontend re-derivation would drift into offering a button that answers 409. An operator who clicks one
 * of those learns to distrust the log, which is worse than not offering it.
 *
 * An already-reverted row keeps both halves of the story visible — the mistake and the correction. That
 * is deliberate: the log is the record of what happened, not a record of the current state.
 */
import { useState } from 'react';

import type { AuditEntry } from '../types';

interface AuditSurfaceProps {
  entries: AuditEntry[];
  onRevert: (entry: AuditEntry) => Promise<string | null>;
  busy: boolean;
}

export function AuditSurface({ entries, onRevert, busy }: AuditSurfaceProps) {
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="border-t border-gray-100 px-4 py-4">
      <h3 className="text-sm font-semibold text-gray-900">Recent changes</h3>
      <p className="mb-2 mt-0.5 text-xs text-gray-500">
        A removal made by mistake can be put back — the log keeps both the mistake and the
        correction.
      </p>

      <div data-testid="audit">
        {entries.length === 0 ? (
          <p className="py-1 text-xs text-gray-500">Nothing has changed yet.</p>
        ) : (
          entries.map((entry) => (
            <div
              key={entry.id}
              data-row
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 py-2 text-sm last:border-b-0"
            >
              <span className="w-32 shrink-0 text-xs text-gray-500">
                {formatWhen(entry.created_at)}
              </span>
              <span className="min-w-0 flex-1">
                <b className="font-medium capitalize">{entry.action}</b>{' '}
                <span className="text-gray-700">{entry.entity_type?.replace(/_/g, ' ')}</span>{' '}
                <span className="font-mono text-xs">{entry.entity_id}</span>
              </span>
              {/* Full address, not a masked one: this is inside the admin-gated tab, and an admin
                  chasing "why did this client lose access?" needs to know who to ask. */}
              <span className="text-xs text-gray-600">{entry.actor_email || 'unattributed'}</span>
              {entry.revertible ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => setError(await onRevert(entry))}
                  className="rounded border border-teal-700 px-2 py-0.5 text-xs font-medium text-teal-800 hover:bg-teal-50 disabled:opacity-40"
                >
                  Revert
                </button>
              ) : entry.reverted_by_audit_id ? (
                <span className="text-xs text-teal-800">
                  reverted by #{entry.reverted_by_audit_id}
                </span>
              ) : (
                <span className="w-14" />
              )}
            </div>
          ))
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {error}
        </p>
      )}
    </section>
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  // Local time, and the date as well as the clock: an audit entry read a week later with only "19:04"
  // on it is a change that happened at an unknown time.
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export default AuditSurface;
